<template>
  <table v-if="selectedProduct" class="tl-volume-pricing">
    <thead>
      <tr>
        <th width="25%">
          {{ t("shared.product.volume_pricing.quantity_table_header") }}
        </th>
        <th :width="showDiscountColumn ? '50%' : '75%'">
          {{ t("shared.product.volume_pricing.price_table_header", [volumePricingMeasureUnit])
          }}{{ priceHeaderTaxLabel }}
        </th>
        <th v-if="showDiscountColumn" width="25%">
          {{ t("shared.product.volume_pricing.discount_table_header") }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(item, index) in selectedProduct?.price.tierPrices" :key="index">
        <td>{{ getQuantityRange(index) }}</td>
        <td>
          <span
            v-if="showLinedThroughTierPriceWithoutDiscount(selectedProduct, item)"
            class="price-line-trough font-medium text-[--color-neutral-a2] line-through"
          >
            {{ tierPriceWithoutDiscount(selectedProduct, item) }}
          </span>
          {{ formattedSuitableTierPrice(selectedProduct, item) }}
        </td>
        <td v-if="showDiscountColumn">{{ getTierDiscountPercentage(item) }}</td>
      </tr>
    </tbody>
  </table>
</template>

<script lang="ts" setup>
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useCurrency } from "@/core/composables";
import { EXCL_VAT_CURRENCIES } from "@/core/constants";
import { formatPrice } from "@/shared/cart/utils/formatPrice";
import type { MoneyType, Product, TierPriceType } from "@/core/api/graphql/types";

interface IProps {
  product: Product;
  hideLadderDiscount?: boolean | undefined;
  showDiscountColumn?: boolean | undefined;
}
const props = withDefaults(defineProps<IProps>(), {
  showDiscountColumn: false,
});
const { t } = useI18n();
const { currentCurrency } = useCurrency();
const selectedProduct = ref<Product | undefined>(undefined);

watchEffect(() => {
  if (props.product) {
    selectedProduct.value = props.product;
  }
});

const volumePricingMeasureUnit = computed(() => {
  if (props.product && (props.product as Product).measureUnit?.toLocaleLowerCase() === "each") {
    return t("shared.product.volume_pricing.unit_title");
  } else if (props.product && (props.product as Product).measureUnit?.toLocaleLowerCase() === "meter") {
    return t("shared.product.volume_pricing.meter_title");
  }

  return t("shared.product.volume_pricing.unit_title");
});

const priceHeaderTaxLabel = computed(() => {
  if (currentCurrency.value.code === "CNY") {
    return t("shared.product.price_with_tax");
  }
  if (EXCL_VAT_CURRENCIES.includes(currentCurrency.value.code)) {
    return t("shared.product.price_excluding_vat");
  }
  return "";
});

function getQuantityRange(index: number) {
  const tierPrices = props.product?.price.tierPrices;
  if (index === 0) {
    return `${tierPrices[index].quantity} ${t("shared.product.calibrated_item.table_to")} ${tierPrices[index + 1].quantity - 1}`;
  } else if (index === tierPrices.length - 1) {
    return `${tierPrices[index].quantity} ${t("shared.product.calibrated_item.or_more")}`;
  } else {
    return `${tierPrices[index].quantity} ${t("shared.product.calibrated_item.table_to")} ${tierPrices[index + 1].quantity - 1}`;
  }
}

function showDiscountedPrice() {
  return (
    !!props.product.price.discountPercent &&
    !props.product.price.discounts?.find((d) => d.description === "LADDER") &&
    !props.hideLadderDiscount
  );
}

function calculateTierPriceWithoutDiscount(discountedTierPrice: number, discountPercent: number) {
  const roundedDiscountPercent = Math.round(discountPercent * 100) / 100;
  return Math.round((discountedTierPrice / (1 - roundedDiscountPercent)) * 100) / 100;
}

function createPriceWithNewAmountWithTax(newAmount: number, oldItem: MoneyType): MoneyType {
  return {
    amount: newAmount + calculateTaxAmount(newAmount),
    currency: oldItem.currency,
    formattedAmount: oldItem.formattedAmount,
    decimalDigits: oldItem.decimalDigits,
    formattedAmountWithoutCurrency: oldItem.formattedAmountWithoutCurrency,
    formattedAmountWithoutPoint: oldItem.formattedAmountWithoutPoint,
    formattedAmountWithoutPointAndCurrency: oldItem.formattedAmountWithoutPointAndCurrency,
  };
}

function isDiscountFromTierPriceExceeded(product: Product, item: TierPriceType) {
  const roundedDiscountPercent = Math.round(product.price?.discountPercent * 100) / 100;
  const noDiscountTierPrice = calculateTierPriceWithoutDiscount(item.price.amount, product.price.discountPercent);
  const quantity = item.quantity;
  const listPrice = product.price.list;

  return (
    product.price?.discountPercent &&
    noDiscountTierPrice * quantity > listPrice.amount * quantity * (1 - roundedDiscountPercent)
  );
}

function showLinedThroughTierPriceWithoutDiscount(product: Product, item: TierPriceType) {
  return showDiscountedPrice() && isDiscountFromTierPriceExceeded(product, item);
}

function getSuitableTierPrice(product: Product, item: TierPriceType): MoneyType {
  return showDiscountedPrice() && !!product.price.discountPercent && isDiscountFromTierPriceExceeded(product, item)
    ? item.price
    : createPriceWithNewAmountWithTax(
        calculateTierPriceWithoutDiscount(item.price.amount, product.price.discountPercent),
        item.price,
      );
}

function tierPriceWithoutDiscount(product: Product, item: TierPriceType) {
  return formatPrice(
    createPriceWithNewAmountWithTax(
      calculateTierPriceWithoutDiscount(item.price.amount, product.price.discountPercent),
      item.price,
    ),
    currentCurrency.value,
  );
}

function formattedSuitableTierPrice(product: Product, item: TierPriceType) {
  return formatPrice(getSuitableTierPrice(product, item), currentCurrency.value);
}

function calculateTaxAmount(amount: number) {
  const actualWithTax = props.product.price.actualWithTax.amount;
  const actual = props.product.price.actual.amount;
  if (currentCurrency.value.code === "CNY" && actualWithTax > actual) {
    const taxRate = ((actualWithTax - actual) / actual) * 100;
    const taxAmount = (amount * taxRate) / 100;
    return Number(taxAmount.toFixed(2));
  }
  return 0;
}

const getTierDiscountPercentage = (item: TierPriceType): string => {
  if (!props.product?.price?.list?.amount || item.price.amount === 0) {
    return "-";
  }

  const listPrice = props.product.price.list.amount + calculateTaxAmount(props.product.price.list.amount);
  const displayedPrice = getSuitableTierPrice(props.product, item);

  const discountPercent = ((listPrice - displayedPrice.amount) / listPrice) * 100;

  if (discountPercent <= 0) {
    return "-";
  }

  return `${Math.round(discountPercent)}%`;
};
</script>

<style lang="scss">
.tl-volume-pricing {
  @apply w-full;

  thead {
    th {
      @apply text-left bg-[var(--color-neutral-a6)] px-4 py-3 font-bold text-[var(--color-neutral-a1)];
      &:nth-child(1) {
        @apply pr-0;
      }
      &:nth-child(2) {
        @apply text-center;
      }
      &:nth-child(3) {
        @apply text-center pl-0;
      }
    }
  }

  td {
    @apply px-4 py-3 border-t border-t-[var(--color-neutral-a4)];
    &:nth-child(1) {
      @apply pr-0;
    }
    &:nth-child(2) {
      @apply text-center;
    }
    &:nth-child(3) {
      @apply text-center pl-0;
    }
  }
}
</style>
